import { api } from '@/lib/api';
import {
  IPDQueueEntry,
  CreateIPDQueueData,
  UpdateIPDQueueStatusData,
  IPDAdmissionData,
  IPDAdmission,
  IPDVisitData,
  IPDVisit,
  Ward,
  Bed,
  InsuranceCompany,
  CreateWardData,
  UpdateWardData,
  UpdateWardBedCountData,
  CreateInsuranceCompanyData,
  IPDQueueResponse,
  IPDAdmissionResponse,
  IPDDashboardStatsResponse,
  WardsResponse,
  InsuranceCompaniesResponse,
  DoctorsResponse,
  DischargeSummaryTemplate,
  IPDDischargeFollowUpItem,
} from '@/types/ipd';

export type IPDBillEditRequestStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export type IpdExcessCollectionRow = {
  admissionId: string;
  ipdNumber: string;
  patientName: string;
  uhid: string | null;
  admissionStatus: string;
  admissionDate: string;
  dischargeDate: string | null;
  billNumber: string | null;
  billStatus: string | null;
  billedAmount: number;
  collectedAmount: number;
  excessAmount: number;
};

export interface IPDBillEditRequest {
  id: string;
  reason: string;
  status: IPDBillEditRequestStatus;
  createdAt: string;
  reviewedAt?: string | null;
  adminNote?: string | null;
  requestedBy?: { id: string; name: string };
  reviewedBy?: { id: string; name: string } | null;
  admissionId: string;
  admission?: {
    id: string;
    queue?: {
      ipdNumber?: string | null;
      patient?: { name: string; uhid: string };
    };
  };
  ipdBill?: { billNumber: string; netAmount: number; status: string };
}

export const ipdApi = {
  // IPD Queue Management
  createQueue: (data: CreateIPDQueueData) =>
    api.post<IPDQueueResponse>('/api/ipd/queue', data),

  getQueue: (params?: { doctorId?: string; status?: string }) =>
    api.get<{ data: IPDQueueEntry[] }>('/api/ipd/queue', { params }),

  getQueueById: (id: string) =>
    api.get<{ data: IPDQueueEntry }>(`/api/ipd/queue/${id}`),

  updateQueueStatus: (id: string, data: UpdateIPDQueueStatusData) =>
    api.patch<IPDQueueResponse>(`/api/ipd/queue/${id}/status`, data),

  updateQueue: (id: string, data: { patientId: string }) =>
    api.patch<IPDQueueResponse>(`/api/ipd/queue/${id}`, data),

  deleteQueue: (id: string) =>
    api.delete<{ message: string; data: { id: string } }>(`/api/ipd/queue/${id}`),

  // IPD Admission Management
  createAdmission: (data: IPDAdmissionData | FormData) =>
    api.post<IPDAdmissionResponse>('/api/ipd/admission', data, {
      timeout: 60000,
      headers:
        data instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' }
    }),

  getAdmissions: (params?: { status?: string }) =>
    api.get<{ data: IPDAdmission[] }>('/api/ipd/admission', { params }),

  getAdmissionById: (id: string) =>
    api.get<{ data: IPDAdmission }>(`/api/ipd/admission/${id}`),

  getAdmissionFormDefaults: (queueId: string, beforeDate?: string) =>
    api.get<{
      data: {
        salesPersonName: string | null;
        leadSource: string | null;
        leadSourceDetails: string | null;
        crmLeadSourceRaw?: string | null;
      };
    }>(`/api/ipd/queue/${queueId}/admission-defaults`, {
      params: beforeDate ? { beforeDate } : undefined,
    }),

  downloadAdmissionFormPDF: (admissionId: string) =>
    api.get(`/api/ipd/admission/${admissionId}/admission-form-pdf`, {
      responseType: 'blob',
      timeout: 60000,
    }),

  updateAdmission: (id: string, data: Partial<IPDAdmissionData> | FormData) =>
    api.patch<IPDAdmissionResponse>(`/api/ipd/admission/${id}`, data, {
      timeout: 60000,
      headers:
        data instanceof FormData
          ? {}
          : { 'Content-Type': 'application/json' },
    }),

  changeAdmissionDoctor: (admissionId: string, assignedDoctorId: string) =>
    api.patch<{
      message: string;
      data: {
        admission: IPDAdmission;
        previousDoctor?: { id: string; name: string; specialisation: string } | null;
        assignedDoctor: { id: string; name: string; specialisation: string };
      };
    }>(`/api/ipd/admission/${admissionId}/assigned-doctor`, { assignedDoctorId }),

  // IPD Visit Management
  createVisit: (data: IPDVisitData) =>
    api.post<{ message: string; data: IPDVisit }>('/api/ipd/visit', data, { timeout: 60000 }),

  getVisits: (admissionId: string) =>
    api.get<{ data: IPDVisit[] }>(`/api/ipd/visit/${admissionId}`),

  // IPD Discharge Management
  createDischargeSummary: (data: {
    admissionId: string;
    dischargeDate: string;
    finalDiagnosis: string;
    treatmentSummary: string;
    proceduresPerformed?: string;
    medicationsPrescribed: string;
    followUpInstructions: string;
    followUpAt?: string | null;
    followUpReason?: string | null;
    remarks?: string;
    doctorSignature?: string;
    hospitalStamp?: string;
    isDraft?: boolean;
    billTrack?: 'REVENUE' | 'OFFLINE';
  }) =>
    api.post<{ message: string; data: any }>('/api/ipd/discharge-summary', data),

  getDischargeSummary: (admissionId: string) =>
    api.get<{ data: any }>(`/api/ipd/discharge-summary/${admissionId}`),

  getDischargeFollowUpSummary: () =>
    api.get<{
      data: { todayCount: number; upcomingCount: number; overdueCount: number };
    }>('/api/ipd/discharge-follow-ups/summary'),

  listDischargeFollowUps: (range: 'today' | 'upcoming' | 'overdue' | 'all' = 'today') =>
    api.get<{ data: IPDDischargeFollowUpItem[] }>('/api/ipd/discharge-follow-ups', {
      params: { range },
    }),

  rescheduleDischargeFollowUp: (
    admissionId: string,
    data: { followUpAt?: string | null; followUpReason?: string | null }
  ) =>
    api.patch<{ message: string; data: IPDDischargeFollowUpItem }>(
      `/api/ipd/discharge-summary/${admissionId}/follow-up`,
      data
    ),

  enableOfflineBilling: (admissionId: string) =>
    api.post<{
      message: string;
      data: { admissionId: string; offlineIpdNumber: string; useOfflineBilling: boolean };
    }>(`/api/ipd/admission/${admissionId}/enable-offline-billing`),

  /** Admin: delete OFFLINE bill and remove from offline queue (does not delete chargeables/medicines). */
  deleteOfflineBilling: (admissionId: string) =>
    api.delete<{
      message: string;
      data: {
        admissionId: string;
        useOfflineBilling: boolean;
        deletedBillId: string | null;
        offlineIpdNumber: string | null;
      };
    }>(`/api/ipd/admission/${admissionId}/offline-billing`),

  /**
   * Admin: hard-delete IPD admission (bills, expenses, clinical IPD data).
   * Medicines return to inventory. Patient master record is kept.
   * confirmIpdNumber must match the IPD # exactly.
   */
  purgeAdmission: (admissionId: string, confirmIpdNumber: string) =>
    api.delete<{
      message: string;
      data: {
        admissionId: string;
        queueId: string;
        ipdNumber: string | null;
        queueNumber?: string | null;
        offlineIpdNumber?: string | null;
        stockReturnedUnits: number;
        pharmacyIssuesCleared: number;
        sequencesReclaimed?: {
          ipd: boolean;
          ipq: boolean;
          oipd: boolean;
        };
      };
    }>(`/api/ipd/admission/${admissionId}`, {
      params: { confirmIpdNumber },
    }),

  getRevenueBillEditLock: (admissionId: string) =>
    api.get<{
      data: {
        locked: boolean;
        reason?: string;
        admissionDischarged: boolean;
        useOfflineBilling: boolean;
        unlockedUntil: string | null;
        pendingRequest: { id: string; reason: string; createdAt: string } | null;
      };
    }>(`/api/ipd/admission/${admissionId}/revenue-bill-edit-lock`),

  createBillEditRequest: (admissionId: string, reason: string) =>
    api.post<{ message: string; data: unknown }>(
      `/api/ipd/admission/${admissionId}/bill-edit-requests`,
      { reason }
    ),

  listExcessCollections: (opts?: {
    status?: 'ALL' | 'ADMITTED' | 'DISCHARGED';
    hospitalId?: string;
  }) =>
    api.get<{
      data: {
        rows: IpdExcessCollectionRow[];
        scannedAdmissionCount: number;
      };
    }>(`/api/ipd/billing/excess-collections`, {
      params: {
        status: opts?.status ?? 'ALL',
        ...(opts?.hospitalId ? { hospitalId: opts.hospitalId } : {}),
      },
    }),

  listBillEditRequests: (opts?: {
    status?: IPDBillEditRequestStatus | 'ALL';
    limit?: number;
  }) =>
    api.get<{ data: IPDBillEditRequest[] }>(`/api/ipd/bill-edit-requests`, {
      params: {
        status: opts?.status ?? 'PENDING',
        ...(opts?.limit != null ? { limit: opts.limit } : {}),
      },
    }),

  approveBillEditRequest: (requestId: string, adminNote?: string) =>
    api.post<{ message: string; data: unknown }>(
      `/api/ipd/bill-edit-requests/${requestId}/approve`,
      { adminNote }
    ),

  rejectBillEditRequest: (requestId: string, adminNote?: string) =>
    api.post<{ message: string; data: unknown }>(
      `/api/ipd/bill-edit-requests/${requestId}/reject`,
      { adminNote }
    ),

  copyOfflineSettlementToRevenue: (
    admissionId: string,
    data?: { freezeRevenueAfter?: boolean }
  ) =>
    api.post<{
      message: string;
      data: {
        admissionId: string;
        offlineBillId: string;
        revenueBillId: string;
        offlineNetAmount: number;
        revenueNetAmount: number;
        itemCount: number;
        auto: boolean;
        syncedAt: string;
      };
    }>(`/api/ipd/admission/${admissionId}/copy-offline-settlement`, data ?? {}),

  getDischargeBlockers: (admissionId: string, billTrack?: 'REVENUE' | 'OFFLINE') =>
    api.get<{
      data: {
        hasUnsettledPharma: boolean;
        unsettledPharmaItems: Array<{
          id: string;
          description: string;
          quantity: number;
          unitRate: unknown;
          chargeCode: string;
          pharmaBillingStatus: string | null;
          billSeparately: boolean;
          serviceDate: string;
        }>;
        hasOutstandingBalance?: boolean;
        outstandingAmount?: number;
        billNet?: number;
        totalDeposits?: number;
      };
    }>(`/api/ipd/admission/${admissionId}/discharge-blockers`, {
      params: billTrack === 'OFFLINE' ? { billTrack: 'OFFLINE' } : {},
    }),

  // Discharge summary templates
  getDischargeTemplates: () =>
    api.get<{ data: DischargeSummaryTemplate[] }>('/api/ipd/discharge-templates'),

  createDischargeTemplate: (data: {
    name: string;
    finalDiagnosis: string;
    treatmentSummary: string;
    proceduresPerformed?: string;
    medicationsPrescribed: string;
    followUpInstructions: string;
    followUpReason?: string | null;
    remarks?: string;
    doctorSignature?: string;
    hospitalStamp?: string;
  }) =>
    api.post<{ message: string; data: DischargeSummaryTemplate }>('/api/ipd/discharge-templates', data),

  updateDischargeTemplate: (id: string, data: {
    name?: string;
    finalDiagnosis?: string;
    treatmentSummary?: string;
    proceduresPerformed?: string;
    medicationsPrescribed?: string;
    followUpInstructions?: string;
    followUpReason?: string | null;
    remarks?: string;
    doctorSignature?: string;
    hospitalStamp?: string;
  }) =>
    api.patch<{ message: string; data: DischargeSummaryTemplate }>(`/api/ipd/discharge-templates/${id}`, data),

  generateDischargeSummaryPDF: (admissionId: string, data: {
    dischargeDate: string;
    finalDiagnosis: string;
    treatmentSummary: string;
    proceduresPerformed?: string;
    medicationsPrescribed: string;
    followUpInstructions: string;
    followUpAt?: string | null;
    followUpReason?: string | null;
    remarks?: string;
    doctorSignature?: string;
    hospitalStamp?: string;
  }, options?: { forPrint?: boolean }) =>
    api.post(
      `/api/ipd/discharge-summary/${admissionId}/generate-pdf${options?.forPrint ? '?forPrint=true' : ''}`,
      data,
      { responseType: 'blob', timeout: 60000 }
    ),

  sendDischargeSummaryWhatsApp: (admissionId: string, data: {
    dischargeDate: string;
    finalDiagnosis: string;
    treatmentSummary: string;
    proceduresPerformed?: string;
    medicationsPrescribed: string;
    followUpInstructions: string;
    followUpAt?: string | null;
    followUpReason?: string | null;
    remarks?: string;
    doctorSignature?: string;
    hospitalStamp?: string;
  }) =>
    api.post<{ message: string; data: { pdfUrl: string; sent: boolean } }>(`/api/ipd/discharge-summary/${admissionId}/send-whatsapp`, data, { timeout: 60000 }),

  // Ward Management
  getWards: () =>
    api.get<WardsResponse>('/api/ipd/ward'),

  createWard: (data: CreateWardData) =>
    api.post<{ message: string; data: Ward }>('/api/ipd/ward', data),

  updateWard: (id: string, data: UpdateWardData) =>
    api.patch<{ message: string; data: Ward }>(`/api/ipd/ward/${id}`, data),

  updateWardBedCount: (id: string, data: UpdateWardBedCountData) =>
    api.patch<{ message: string; data: Ward }>(`/api/ipd/ward/${id}/bed-count`, data),

  // Bed Management
  getAvailableBeds: (wardId: string) =>
    api.get<{ message: string; data: Bed[] }>(`/api/ipd/ward/${wardId}/beds`),

  // IPD Patient Document APIs
  uploadIPDPatientDocument: (data: FormData) =>
    api.post<{ message: string; data: any }>('/api/ipd/patient-document', data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  
  getIPDPatientDocuments: (admissionId: string) =>
    api.get<{ message: string; data: any[] }>(`/api/ipd/patient-document/${admissionId}`),
  
  deleteIPDPatientDocument: (documentId: string) =>
    api.delete<{ message: string }>(`/api/ipd/patient-document/${documentId}`),

  // Insurance Companies
  getInsuranceCompanies: () =>
    api.get<InsuranceCompaniesResponse>('/api/ipd/insurance-company'),

  createInsuranceCompany: (data: CreateInsuranceCompanyData) =>
    api.post<{ message: string; data: InsuranceCompany }>('/api/ipd/insurance-company', data),

  updateInsuranceCompany: (id: string, data: Partial<CreateInsuranceCompanyData>) =>
    api.patch<{ message: string; data: InsuranceCompany }>(`/api/ipd/insurance-company/${id}`, data),

  deleteInsuranceCompany: (id: string) =>
    api.delete<{ message: string }>(`/api/ipd/insurance-company/${id}`),

  // Dashboard Statistics
  getDashboardStats: () =>
    api.get<IPDDashboardStatsResponse>('/api/ipd/dashboard/stats'),

  // Doctor and Patient data
  getDoctors: () =>
    api.get<DoctorsResponse>('/api/doctor/get-by-hospital'),

  quickCreateDoctor: (data: { name: string; specialisation?: string }) =>
    api.post<{ message: string; data: { id: string; name: string; specialisation?: string | null } }>(
      '/api/doctor/quick-create',
      data
    ),

  getPatients: (params?: { search?: string }) =>
    api.get<{ data: any[] }>('/api/patient', { params }),

  // Comprehensive Patient Details
  getPatientDetails: (admissionId: string) =>
    api.get<{
      data: {
        admission: IPDAdmission;
        ipdDocuments: any[];
        ipdVisits: IPDVisit[];
        ipdLabTests: any[];
        ipdSurgeries: any[];
        opdLabTests: any[];
        opdSurgeries: any[];
        patientDocuments: any[];
      };
    }>(`/api/ipd/patient-details/${admissionId}`),

  // IPD Lab Test Management
  createIPDLabTest: (data: {
    admissionId: string;
    testName: string;
    testCode?: string;
    category?: string;
    priority: 'ROUTINE' | 'URGENT' | 'STAT';
    instructions?: string;
    fastingRequired?: boolean;
    fastingHours?: number;
    specialInstructions?: string;
    testCost?: number;
    labTestId?: string;
    processingType?: 'INTERNAL' | 'EXTERNAL';
    externalVendorId?: string;
    externalCost?: number;
    externalInvoiceId?: string;
  }) =>
    api.post<{ message: string; data: any }>('/api/ipd/lab-test', data),

  getIPDLabTestsByHospital: (params?: { status?: string; hospitalId?: string }) =>
    api.get<{ message: string; data: any[] }>('/api/ipd/lab-test/hospital/all', { params }),

  listLabExternalVendors: (params?: { hospitalId?: string }) =>
    api.get<{ message: string; data: any[] }>('/api/ipd/lab-external-vendors', { params }),

  updateIPDLabTest: (id: string, data: {
    status?: string;
    scheduledAt?: string;
    completedAt?: string;
    resultDate?: string;
    resultValue?: string;
    resultUnit?: string;
    normalRange?: string;
    abnormalFlag?: boolean;
    resultNotes?: string;
    performedById?: string;
    processingType?: 'INTERNAL' | 'EXTERNAL';
    externalVendorId?: string | null;
    externalCost?: number | null;
    externalInvoiceId?: string | null;
    /** Patient charge on IPD bill (synced via accrual). */
    testCost?: number | null;
  }) =>
    api.patch<{ message: string; data: any }>(`/api/ipd/lab-test/${id}`, data),

  deleteIPDLabTest: (id: string) =>
    api.delete<{ message: string; data: any }>(`/api/ipd/lab-test/${id}`),

  uploadIPDLabTestAttachment: (labTestId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('labTestId', labTestId);
    formData.append('attachmentType', 'RESULT_REPORT');
    return api.post<{ message: string; data: any }>('/api/ipd/lab-test/attachment', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  deleteIPDLabTestAttachment: (attachmentId: string) =>
    api.delete<{ message: string; data: any }>(`/api/ipd/lab-test/attachment/${attachmentId}`),

  // IPD Lab external vendors
  createLabExternalVendor: (
    body: { name: string; phone?: string; address?: string; gstNo?: string; email?: string },
    params?: { hospitalId?: string }
  ) =>
    api.post<{ message: string; data: any }>('/api/ipd/lab-external-vendors', body, { params }),
  updateLabExternalVendor: (
    vendorId: string,
    body: { name?: string; phone?: string; address?: string; gstNo?: string; email?: string },
    params?: { hospitalId?: string }
  ) =>
    api.patch<{ message: string; data: any }>(
      `/api/ipd/lab-external-vendors/${vendorId}`,
      body,
      { params }
    ),
  deleteLabExternalVendor: (vendorId: string, params?: { hospitalId?: string }) =>
    api.delete<{ message: string; data: any }>(
      `/api/ipd/lab-external-vendors/${vendorId}`,
      { params }
    ),
  listLabExternalVendorInvoices: (vendorId: string) =>
    api.get<{ message: string; data: any[] }>(`/api/ipd/lab-external-vendors/${vendorId}/invoices`),
  createLabExternalVendorInvoice: (
    vendorId: string,
    formData: FormData,
    params?: { hospitalId?: string }
  ) =>
    api.post<{ message: string; data: any }>(
      `/api/ipd/lab-external-vendors/${vendorId}/invoices`,
      formData,
      { params }
    ),

  // IPD Surgery Management
  createIPDSurgery: (data: {
    admissionId: string;
    surgeryName: string;
    surgeryCode?: string;
    category?: string;
    priority: 'ROUTINE' | 'URGENT' | 'STAT' | 'EMERGENCY';
    scheduledAt?: string;
    estimatedDuration?: number;
    procedureDescription?: string;
    [key: string]: any;
  }) =>
    api.post<{ message: string; data: any }>('/api/ipd/surgery', data),

  getIPDSurgeries: (admissionId: string) =>
    api.get<{ data: any[] }>(`/api/ipd/surgery/${admissionId}`),

  updateIPDSurgery: (id: string, data: { status?: string; scheduledAt?: string; [key: string]: any }) =>
    api.patch<{ message: string; data: any }>(`/api/ipd/surgery/${id}`, data),
};
